import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Modal,
  FlatList
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { CommonActions } from '@react-navigation/native';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { Exercise } from '../../types';
import { useTheme } from '../../context/ThemeContext';

type Props = NativeStackScreenProps<MainStackParamList, 'CreateRoutine'>;

interface RoutineDay {
  order: number;
  name: string;
  exercises: RoutineExercise[];
}

interface RoutineExercise {
  exerciseId: string;
  exerciseName: string;
  series: number;
  repetitions: number;
  restTime: number; // in seconds
  muscleGroups?: string[];
}

const CreateRoutineScreen = ({ navigation }: Props) => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState<RoutineDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(true);
  const [currentDay, setCurrentDay] = useState<RoutineDay | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [dayName, setDayName] = useState('');
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [series, setSeries] = useState('');
  const [repetitions, setRepetitions] = useState('');
  const [restTime, setRestTime] = useState('');
  const [editingExerciseIndex, setEditingExerciseIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      setExercisesLoading(true);
      const exercisesCollection = collection(firestore, 'exercises');
      const exercisesQuery = query(exercisesCollection);
      const querySnapshot = await getDocs(exercisesQuery);
      
      const exercisesList: Exercise[] = [];
      querySnapshot.forEach((doc) => {
        exercisesList.push({
          id: doc.id,
          ...doc.data()
        } as Exercise);
      });
      
      setExercises(exercisesList);
    } catch (error) {
      console.error('Error al obtener ejercicios:', error);
      Alert.alert('Error', 'No se pudieron cargar los ejercicios');
    } finally {
      setExercisesLoading(false);
    }
  };

  const addDay = () => {
    if (!dayName.trim()) {
      Alert.alert('Error', 'Por favor ingresa un nombre para el día');
      return;
    }
    
    const newDay: RoutineDay = {
      order: days.length + 1,
      name: dayName,
      exercises: []
    };
    
    setDays([...days, newDay]);
    setDayName('');
    setShowDayModal(false);
  };

  const editDay = (index: number) => {
    setCurrentDay(days[index]);
  };

  const removeDay = (index: number) => {
    Alert.alert(
      'Confirmar',
      '¿Estás seguro de eliminar este día?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Eliminar', 
          style: 'destructive',
          onPress: () => {
            const newDays = [...days];
            newDays.splice(index, 1);
            
            // Reordenar los días
            newDays.forEach((day, i) => {
              day.order = i + 1;
            });
            
            setDays(newDays);
          }
        }
      ]
    );
  };

  const addExerciseToDay = () => {
    if (!currentDay) return;
    if (!selectedExercise) {
      Alert.alert('Error', 'Por favor selecciona un ejercicio');
      return;
    }
    
    if (!series.trim() || !repetitions.trim()) {
      Alert.alert('Error', 'Por favor completa las series y repeticiones');
      return;
    }
    
    const seriesValue = parseInt(series);
    const repetitionsValue = parseInt(repetitions);
    const restTimeValue = restTime.trim() ? parseInt(restTime) : 60;
    
    if (seriesValue <= 0 || repetitionsValue <= 0) {
      Alert.alert('Error', 'Las series y repeticiones deben ser mayores a 0');
      return;
    }
    
    const newExercise: RoutineExercise = {
      exerciseId: selectedExercise.id,
      exerciseName: selectedExercise.name,
      series: seriesValue,
      repetitions: repetitionsValue,
      restTime: restTimeValue,
      muscleGroups: selectedExercise.muscleGroups
    };
    
    const dayIndex = days.findIndex(d => d.order === currentDay.order);
    if (dayIndex === -1) return;
    
    const newDays = [...days];
    
    if (editingExerciseIndex !== null) {
      // Editing existing exercise
      newDays[dayIndex].exercises[editingExerciseIndex] = newExercise;
    } else {
      // Adding new exercise
      newDays[dayIndex].exercises.push(newExercise);
    }
    
    setDays(newDays);
    setCurrentDay(newDays[dayIndex]);
    clearExerciseForm();
    setShowExerciseModal(false);
  };

  const prepareEditExercise = (exerciseIndex: number) => {
    if (!currentDay) return;
    
    const exercise = currentDay.exercises[exerciseIndex];
    setSelectedExercise({
      id: exercise.exerciseId,
      name: exercise.exerciseName,
      muscleGroups: exercise.muscleGroups || [],
      difficultyLevel: 'intermediate', // Default as we don't have this info
	  description: '',
	  instructions: '',
	  createdBy: '',
	  createdAt: new Date()
    });
    
    setSeries(exercise.series.toString());
    setRepetitions(exercise.repetitions.toString());
    setRestTime(exercise.restTime.toString());
    setEditingExerciseIndex(exerciseIndex);
    setShowExerciseModal(true);
  };

  const removeExercise = (exerciseIndex: number) => {
    if (!currentDay) return;
    
    Alert.alert(
      'Confirmar',
      '¿Estás seguro de eliminar este ejercicio?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Eliminar', 
          style: 'destructive',
          onPress: () => {
            const dayIndex = days.findIndex(d => d.order === currentDay.order);
            if (dayIndex === -1) return;
            
            const newDays = [...days];
            newDays[dayIndex].exercises.splice(exerciseIndex, 1);
            
            setDays(newDays);
            setCurrentDay(newDays[dayIndex]);
          }
        }
      ]
    );
  };

  const clearExerciseForm = () => {
    setSelectedExercise(null);
    setSeries('');
    setRepetitions('');
    setRestTime('');
    setEditingExerciseIndex(null);
  };

  const validateForm = () => {
    setError(null);
    
    if (!name.trim()) {
      setError('Por favor ingresa un nombre para la rutina');
      return false;
    }
    
    if (!description.trim()) {
      setError('Por favor ingresa una descripción');
      return false;
    }
    
    if (days.length === 0) {
      setError('Por favor agrega al menos un día a la rutina');
      return false;
    }
    
    // Check if any day has no exercises
    const emptyDay = days.find(day => day.exercises.length === 0);
    if (emptyDay) {
      setError(`El día "${emptyDay.name}" no tiene ejercicios`);
      return false;
    }
    
    return true;
  };

  const createRoutine = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const routineData = {
        name,
        description,
        creatorId: userId,
        shared: false,
        authorizedUsers: [userId],
        daysPerWeek: days.length,
        days: days.map(day => ({
          order: day.order,
          name: day.name,
          exercises: day.exercises.map(ex => ({
            exerciseId: ex.exerciseId,
            series: ex.series,
            repetitions: ex.repetitions,
            restTime: ex.restTime
          }))
        })),
        createdAt: new Date()
      };
      
      await addDoc(collection(firestore, 'routines'), routineData);
      
      // Navegar a la pestaña de Routines y resetear la navegación
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [
            { name: 'MainTabs', params: { screen: 'Routines', params: { reload: true } } },
          ],
        })
      );
    } catch (error) {
      console.error('Error al crear rutina:', error);
      setError('No se pudo crear la rutina');
    } finally {
      setLoading(false);
    }
  };

  const renderDayItem = ({ item, index }: { item: RoutineDay; index: number }) => (
    <Card style={styles.dayCard}>
      <View style={styles.dayHeader}>
        <Text style={[styles.dayTitle, { color: theme.colors.text }]}>Día {item.order}: {item.name}</Text>
        <View style={styles.dayActions}>
          <TouchableOpacity
            style={styles.dayAction}
            onPress={() => {
              setCurrentDay(item);
              editDay(index);
            }}
          >
            <Ionicons name="create-outline" size={24} color={theme.colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.dayAction}
            onPress={() => removeDay(index)}
          >
            <Ionicons name="trash-outline" size={24} color={theme.colors.error} />
          </TouchableOpacity>
        </View>
      </View>
      
      {item.exercises.length > 0 ? (
        <View style={styles.exercisesList}>
          {item.exercises.map((exercise, exIndex) => (
            <View key={exercise.exerciseId} style={[styles.exerciseItem, { borderBottomColor: theme.colors.grey.light }]}>
              <View style={styles.exerciseInfo}>
                <Text style={[styles.exerciseName, { color: theme.colors.text }]}>{exercise.exerciseName}</Text>
                <Text style={[styles.exerciseDetails, { color: theme.colors.grey.dark }]}>
                  {exercise.series} series x {exercise.repetitions} repeticiones
                </Text>
                <Text style={[styles.exerciseRest, { color: theme.colors.grey.dark }]}>
                  Descanso: {exercise.restTime} segundos
                </Text>
              </View>
              <View style={styles.exerciseActions}>
                <TouchableOpacity
                  style={styles.exerciseAction}
                  onPress={() => prepareEditExercise(exIndex)}
                >
                  <Ionicons name="create-outline" size={20} color={theme.colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.exerciseAction}
                  onPress={() => removeExercise(exIndex)}
                >
                  <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      ) : (
        <View style={styles.emptyExercises}>
          <Text style={[styles.emptyExercisesText, { color: theme.colors.grey.dark }]}>No hay ejercicios</Text>
        </View>
      )}
      
      <Button
        title="Agregar Ejercicio"
        onPress={() => {
          setCurrentDay(item);
          clearExerciseForm();
          setShowExerciseModal(true);
        }}
        type="outline"
        style={styles.addExerciseButton}
      />
    </Card>
  );

  const renderExerciseItem = ({ item }: { item: Exercise }) => (
    <TouchableOpacity
      style={[
        styles.exerciseSelectItem,
        { borderBottomColor: theme.colors.grey.light },
        selectedExercise?.id === item.id && [styles.exerciseSelectItemSelected, { backgroundColor: theme.colors.grey.light }]
      ]}
      onPress={() => setSelectedExercise(item)}
    >
      <View style={styles.exerciseSelectInfo}>
        <Text style={[styles.exerciseSelectName, { color: theme.colors.text }]}>{item.name}</Text>
        <View style={styles.exerciseSelectMuscles}>
          {item.muscleGroups.slice(0, 3).map((muscle, index) => (
            <Text key={index} style={[styles.exerciseSelectMuscle, { color: theme.colors.grey.dark }]}>
              {muscle}{index < Math.min(item.muscleGroups.length, 3) - 1 ? ', ' : ''}
            </Text>
          ))}
          {item.muscleGroups.length > 3 && (
            <Text style={[styles.exerciseSelectMuscle, { color: theme.colors.grey.dark }]}>...</Text>
          )}
        </View>
      </View>
      {selectedExercise?.id === item.id && (
        <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
      )}
    </TouchableOpacity>
  );

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView style={[styles.scrollView, { backgroundColor: theme.colors.background }]}>
        <View style={styles.container}>
          <Card style={styles.card}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Información Básica</Text>
            
            <Input
              label="Nombre de la Rutina"
              value={name}
              onChangeText={setName}
              placeholder="Ej. Rutina de Fuerza"
              darkMode={true}
            />
            
            <Input
              label="Descripción"
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={3}
              placeholder="Describe el objetivo de esta rutina..."
              darkMode={true}
            />
          </Card>
          
          <View style={styles.daysHeader}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Días de la Rutina</Text>
            <TouchableOpacity
              style={styles.addDayButton}
              onPress={() => setShowDayModal(true)}
            >
              <Ionicons name="add-circle" size={24} color={theme.colors.primary} />
              <Text style={[styles.addDayText, { color: theme.colors.primary }]}>Agregar Día</Text>
            </TouchableOpacity>
          </View>
          
          {days.length > 0 ? (
            days.map((day, index) => renderDayItem({ item: day, index }))
          ) : (
            <Card style={styles.emptyDaysCard}>
              <Text style={[styles.emptyDaysText, { color: theme.colors.grey.dark }]}>
                No hay días en esta rutina. Agrega un día para comenzar.
              </Text>
            </Card>
          )}
          
          {error && (
            <Card style={{
              ...styles.errorCard,
              backgroundColor: theme.colors.error + '15',
              borderColor: theme.colors.error
            }}>
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle" size={24} color={theme.colors.error} />
                <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>
              </View>
            </Card>
          )}
          
          <Button
            title="Crear Rutina"
            onPress={createRoutine}
            type="primary"
            loading={loading}
            style={styles.createButton}
          />
        </View>
      </ScrollView>
      
      {/* Add Day Modal */}
      <Modal
        visible={showDayModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowDayModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Agregar Día</Text>
              <TouchableOpacity onPress={() => setShowDayModal(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>
            
            <Input
              label="Nombre del Día"
              value={dayName}
              onChangeText={setDayName}
              placeholder="Ej. Pecho y Espalda"
              darkMode={true}
            />
            
            <Button
              title="Agregar"
              onPress={addDay}
              type="primary"
              style={styles.modalButton}
            />
          </View>
        </View>
      </Modal>
      
      {/* Add Exercise Modal */}
      <Modal
        visible={showExerciseModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          clearExerciseForm();
          setShowExerciseModal(false);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.exerciseModalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {editingExerciseIndex !== null ? 'Editar Ejercicio' : 'Agregar Ejercicio'}
              </Text>
              <TouchableOpacity 
                onPress={() => {
                  clearExerciseForm();
                  setShowExerciseModal(false);
                }}
              >
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>
            
            {selectedExercise ? (
              <View style={[styles.selectedExerciseContainer, { backgroundColor: theme.colors.grey.light }]}>
                <Text style={[styles.selectedExerciseName, { color: theme.colors.text }]}>{selectedExercise.name}</Text>
                <TouchableOpacity
                  style={styles.changeExerciseButton}
                  onPress={() => setSelectedExercise(null)}
                >
                  <Text style={[styles.changeExerciseText, { color: theme.colors.primary }]}>Cambiar</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View>
                <Text style={[styles.exerciseSelectTitle, { color: theme.colors.text }]}>Selecciona un Ejercicio</Text>
                {exercisesLoading ? (
                  <ActivityIndicator size="large" color={theme.colors.primary} />
                ) : (
                  <FlatList
                    data={exercises}
                    renderItem={renderExerciseItem}
                    keyExtractor={(item) => item.id}
                    style={styles.exerciseSelectList}
                  />
                )}
              </View>
            )}
            
            {selectedExercise && (
              <View style={styles.exerciseFormContainer}>
                <View style={styles.exerciseFormRow}>
                  <View style={styles.formHalf}>
                    <Input
                      label="Series"
                      value={series}
                      onChangeText={setSeries}
                      keyboardType="numeric"
                      placeholder="Ej. 3"
                      darkMode={true}
                    />
                  </View>
                  <View style={styles.formHalf}>
                    <Input
                      label="Repeticiones"
                      value={repetitions}
                      onChangeText={setRepetitions}
                      keyboardType="numeric"
                      placeholder="Ej. 12"
                      darkMode={true}
                    />
                  </View>
                </View>
                
                <Input
                  label="Tiempo de Descanso (segundos)"
                  value={restTime}
                  onChangeText={setRestTime}
                  keyboardType="numeric"
                  placeholder="Ej. 60"
                  darkMode={true}
                />
                
                <Button
                  title={editingExerciseIndex !== null ? "Guardar Cambios" : "Agregar"}
                  onPress={addExerciseToDay}
                  type="primary"
                  style={styles.modalButton}
                />
              </View>
            )}
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    padding: spacing.lg,
  },
  card: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  daysHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  addDayButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addDayText: {
    marginLeft: spacing.xs,
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  dayCard: {
    marginBottom: spacing.md,
  },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  dayTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  dayActions: {
    flexDirection: 'row',
  },
  dayAction: {
    marginLeft: spacing.sm,
  },
  exercisesList: {
    marginBottom: spacing.md,
  },
  exerciseItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
  },
  exerciseInfo: {
    flex: 1,
  },
  exerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    marginBottom: spacing.xs,
  },
  exerciseDetails: {
    fontSize: typography.fontSize.sm,
  },
  exerciseRest: {
    fontSize: typography.fontSize.sm,
  },
  exerciseActions: {
    flexDirection: 'row',
  },
  exerciseAction: {
    marginLeft: spacing.sm,
    padding: spacing.xs,
  },
  emptyExercises: {
    padding: spacing.md,
    alignItems: 'center',
  },
  emptyExercisesText: {
    fontSize: typography.fontSize.md,
  },
  addExerciseButton: {
    marginTop: spacing.xs,
  },
  emptyDaysCard: {
    marginBottom: spacing.lg,
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyDaysText: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
  },
  createButton: {
    marginTop: spacing.lg,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    borderRadius: borderRadius.md,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 400,
  },
  exerciseModalContent: {
    borderRadius: borderRadius.md,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 400,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  modalButton: {
    marginTop: spacing.md,
  },
  exerciseSelectTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    marginBottom: spacing.md,
  },
  exerciseSelectList: {
    maxHeight: 200,
  },
  exerciseSelectItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
  },
  exerciseSelectItemSelected: {
    
  },
  exerciseSelectInfo: {
    flex: 1,
  },
  exerciseSelectName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    marginBottom: spacing.xs,
  },
  exerciseSelectMuscles: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  exerciseSelectMuscle: {
    fontSize: typography.fontSize.sm,
  },
  selectedExerciseContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    marginBottom: spacing.md,
  },
  selectedExerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  changeExerciseButton: {
    padding: spacing.xs,
  },
  changeExerciseText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
  },
  exerciseFormContainer: {
    marginTop: spacing.md,
  },
  exerciseFormRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  formHalf: {
    width: '48%',
  },
  errorCard: {
    marginBottom: spacing.md,
    borderWidth: 1,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
  },
  errorText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.md,
    flex: 1,
  },
});

export default CreateRoutineScreen; 